import type {
  Block,
  EditableBlock,
  HeadingBlock,
  HeadingLevel,
  ImageBlock,
  LinkCardBlock,
  MarginBlock,
  LegacyPinnedBlock,
  MarginNoteBlock,
  PinSide,
  TextBlock,
  Thought,
  ThoughtBlock,
  ThoughtLikeBlock,
  UnfoldBlock,
} from './types'
import { escapeHtml, hostOf, stripTags, thoughtKind } from './text'
import { cleanAttribution } from './sanitize'

export const REFLECTION_SUBTITLE = 'a reflection, stitched from what I saved along the way'

export function newBlockId(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return 'b' + c.randomUUID().replace(/-/g, '').slice(0, 10)
  return 'b' + Math.random().toString(36).slice(2, 12)
}

export const emptyText = (): TextBlock => ({ id: newBlockId(), type: 'text', html: '' })

export function isEditable(b: Block): b is EditableBlock {
  return b.type === 'text' || b.type === 'heading' || b.type === 'pullquote'
}

export function isBlockEmpty(b: Block): boolean {
  if (isEditable(b)) return stripTags(b.html).trim() === ''
  if (b.type === 'divider') return false
  return false
}

export function headingLevel(b: Pick<HeadingBlock, 'level'>): HeadingLevel {
  return b.level === 1 || b.level === 3 ? b.level : 2
}

/** A heading's type classes (index.css): the shared heading face at its level's size. */
export function headingClass(level: HeadingLevel): string {
  return level === 1 ? 'wr-t-heading wr-t-h1' : level === 3 ? 'wr-t-heading wr-t-h3' : 'wr-t-heading'
}

/** A pull quote's type classes (index.css): centred italic, at its level's heading size. */
export function pullquoteClass(level: HeadingLevel): string {
  return level === 1 ? 'wr-t-pullquote wr-t-q1' : level === 3 ? 'wr-t-pullquote wr-t-q3' : 'wr-t-pullquote'
}

/** Who said a pull quote, cleaned (lib/sanitize.ts); undefined for any other block, or a quote without one. */
export function quoteAttribution(b: Block | undefined): string | undefined {
  return b?.type === 'pullquote' ? cleanAttribution(b.attribution) : undefined
}

/** The element a heading renders as: h1 is the writing's title, so levels 1–3 are h2–h4. */
export function headingTag(level: HeadingLevel): 'h2' | 'h3' | 'h4' {
  return level === 1 ? 'h2' : level === 3 ? 'h4' : 'h3'
}

/** Rendered beside its anchor (a margin) rather than in the column. */
export function isMarginBlock(b: Block): b is MarginBlock {
  return b.type === 'margin' || b.type === 'voice'
}

/** A margin note pinned across paragraphs: it has a span. */
export type PinnedNote = MarginNoteBlock & { span: number }

/** A pinned margin note (sticky in the left margin beside `span` paragraphs). */
export function isPinnedNote(b: Block | undefined): b is PinnedNote {
  return b?.type === 'margin' && typeof b.span === 'number' && b.span >= 1
}

/** Not part of the column flow: margin blocks, and unfolds (tucked under words). */
export function isOutOfFlow(b: Block): boolean {
  return isMarginBlock(b) || b.type === 'unfold' || isLegacyPin(b)
}

/** Blocks that snapshot a thought's text (quotable, sourceable). */
export function isThoughtLike(b: Block): b is ThoughtLikeBlock {
  return b.type === 'thought' || b.type === 'margin' || b.type === 'unfold'
}

/** Paragraph-like column blocks: what a pin's span counts and narration reads. */
export function isParagraph(b: Block): boolean {
  return b.type === 'text' || b.type === 'pullquote' || b.type === 'thought'
}

/**
 * The column block a margin/unfold block at `index` annotates: the nearest
 * in-flow block above it, or null when there is none.
 */
export function anchorIndex(blocks: Block[], index: number): number | null {
  for (let i = index - 1; i >= 0; i--) if (!isOutOfFlow(blocks[i])) return i
  return null
}

/** Right-margin items (margin notes and voice memos; pinned notes go left), grouped by the id of the column block they sit beside. */
export function marginsByAnchor(blocks: Block[]): Map<string, MarginBlock[]> {
  const out = new Map<string, MarginBlock[]>()
  blocks.forEach((b, i) => {
    if (!isMarginBlock(b) || isPinnedNote(b)) return
    const a = anchorIndex(blocks, i)
    const key = a === null ? '' : blocks[a].id
    out.set(key, [...(out.get(key) ?? []), b])
  })
  return out
}

/** Where a pin's range ends: it never reaches past a scene, a divider, or a cell's edge. */
const endsPin = (b: Block) => b.type === 'scene' || b.type === 'divider' || b.type === 'columns'

/**
 * For each pinned margin note, the ids of the in-flow blocks it stays
 * beside. It starts at the block it's on (its anchor) and covers `span`
 * paragraphs, headings and images in between riding along uncounted. It
 * stops early at the next pinned note's block, a scene, a divider or a
 * cell's edge, so ranges never overlap. A second note pinned on the same
 * block, or one with nothing to stay beside, gets an empty range (it shows
 * inline, where it's stored).
 */
export function pinnedRanges(blocks: Block[]): Map<string, string[]> {
  const out = new Map<string, string[]>()
  const anchorOf = new Map<string, number>()
  // The first pinned note on a block owns the range that starts there.
  const owner = new Map<string, string>()
  blocks.forEach((b, i) => {
    if (!isPinnedNote(b)) return
    const a = anchorIndex(blocks, i)
    if (a === null || endsPin(blocks[a])) return
    anchorOf.set(b.id, a)
    if (!owner.has(blocks[a].id)) owner.set(blocks[a].id, b.id)
  })
  blocks.forEach((pin) => {
    if (!isPinnedNote(pin)) return
    const ids: string[] = []
    const a = anchorOf.get(pin.id)
    if (a !== undefined && owner.get(blocks[a].id) === pin.id) {
      let paragraphs = 0
      for (let j = a; j < blocks.length; j++) {
        const b = blocks[j]
        if (endsPin(b)) break
        if (isOutOfFlow(b)) continue
        if (j > a && owner.has(b.id)) break
        // Span reached: stop before anything else (a following heading belongs to the next section).
        if (paragraphs >= pin.span) break
        ids.push(b.id)
        if (isParagraph(b)) paragraphs++
      }
    }
    out.set(pin.id, ids)
  })
  return out
}

/** How many paragraphs a pinned note currently covers (for the "PINNED · N PARAGRAPHS" label). */
export function pinnedParagraphCount(blocks: Block[], pin: MarginNoteBlock): number {
  const ids = new Set(pinnedRanges(blocks).get(pin.id) ?? [])
  return blocks.filter((b) => ids.has(b.id) && isParagraph(b)).length
}

/**
 * Every block a margin note could stay beside if it were pinned as far as it
 * can go (to the next pinned note, scene, divider, cell edge or the end),
 * each with the span that reaches it: how far the paragraph count can go.
 */
export function pinReach(blocks: Block[], noteId: string): Array<{ id: string; span: number }> {
  const probe = blocks.map((b) => (b.id === noteId && b.type === 'margin' ? { ...b, span: Number.MAX_SAFE_INTEGER } : b))
  const ids = pinnedRanges(probe).get(noteId) ?? []
  const byId = new Map(blocks.map((b) => [b.id, b]))
  let paragraphs = 0
  return ids.map((id) => {
    const b = byId.get(id)
    if (b && isParagraph(b)) paragraphs++
    return { id, span: Math.max(1, paragraphs) }
  })
}

/** The margin a pinned note sits in: the left unless it says right. */
export function pinSide(b: MarginNoteBlock): PinSide {
  return b.side === 'right' ? 'right' : 'left'
}

/** The most paragraphs a margin note can be pinned across, its own included (0: nowhere to pin it). */
export function maxPinSpan(blocks: Block[], noteId: string): number {
  const reach = pinReach(blocks, noteId)
  return reach.length ? reach[reach.length - 1].span : 0
}

// ── Old pins ─────────────────────────────────────────────────────────────

/** An old 'pinned' block (LegacyPinnedBlock), from a writing saved before pins became margin notes. */
export function isLegacyPin(b: unknown): b is LegacyPinnedBlock {
  return !!b && typeof b === 'object' && (b as { type?: unknown }).type === 'pinned'
}

/** The old rule: from the first in-flow block AFTER the pin, `span` paragraphs, or without one until the next heading. */
function legacyRange(blocks: Block[], i: number, span: number | undefined): string[] {
  const ids: string[] = []
  let paragraphs = 0
  for (let j = i + 1; j < blocks.length; j++) {
    const b = blocks[j]
    if (isLegacyPin(b) || endsPin(b)) break
    if (isOutOfFlow(b)) continue
    if (span === undefined && b.type === 'heading' && ids.length > 0) break
    if (span !== undefined && paragraphs >= span) break
    ids.push(b.id)
    if (isParagraph(b)) paragraphs++
  }
  return ids
}

/**
 * Old pins become pinned margin notes covering the same paragraphs. Each
 * moves to just after the first block it covered (so that block is the one
 * it's on), with `span` set to the paragraphs it covered, which also settles
 * the old "until the next heading" as a number. A pin that covered nothing
 * stays where it was, so it pins beside the block above it, if any. Same
 * id, words and source, so readers' takes still find it. Returns the same
 * array when there are none.
 */
export function upgradePins(blocks: Block[]): Block[] {
  if (!Array.isArray(blocks) || !blocks.some(isLegacyPin)) return blocks
  const notes = new Map<string, { note: MarginNoteBlock; after: string | null }>()
  blocks.forEach((b, i) => {
    if (!isLegacyPin(b)) return
    const old: LegacyPinnedBlock = b
    const span = typeof old.span === 'number' && old.span >= 1 ? Math.floor(old.span) : undefined
    const ids = legacyRange(blocks, i, span)
    const covered = new Set(ids)
    const paragraphs = blocks.filter((x) => covered.has(x.id) && isParagraph(x)).length
    const note: MarginNoteBlock = { id: old.id, type: 'margin', text: typeof old.text === 'string' ? old.text : '', span: Math.max(1, ids.length ? paragraphs : (span ?? 1)) }
    if (old.source) note.source = old.source
    if (old.own) note.own = true
    if (old.thoughtId) note.thoughtId = old.thoughtId
    if (old.capturedAt) note.capturedAt = old.capturedAt
    if (old.kind) note.kind = old.kind
    notes.set(old.id, { note, after: ids[0] ?? null })
  })
  // In place first (a pin with nothing to cover stays put), then each one that covered something moves after its first block.
  let out = blocks.map((b) => notes.get(b.id)?.note ?? b)
  for (const { note, after } of notes.values()) {
    if (!after) continue
    out = out.filter((b) => b.id !== note.id)
    out.splice(out.findIndex((b) => b.id === after) + 1, 0, note)
  }
  return out
}

/** Unfold anchors are short ids; sanitize.ts drops any other data-unfold value. */
export const UNFOLD_ANCHOR_RE = /^[A-Za-z0-9_-]{1,40}$/

export function newUnfoldAnchor(): string {
  return 'u' + newBlockId().slice(1, 9)
}

/** The unfold hanging from `<span data-unfold="anchor">`, if any. */
export function unfoldFor(blocks: Block[], anchor: string): UnfoldBlock | undefined {
  return blocks.find((b): b is UnfoldBlock => b.type === 'unfold' && b.anchor === anchor)
}

/** Unfold anchors present in an html string. */
export function unfoldAnchorsIn(html: string): string[] {
  return Array.from(html.matchAll(/data-unfold="([A-Za-z0-9_-]+)"/g), (m) => m[1])
}

/** Remove the `<span data-unfold="anchor">` wrapper, keeping its words. */
export function unwrapUnfold(html: string, anchor: string): string {
  if (!UNFOLD_ANCHOR_RE.test(anchor)) return html
  const tpl = document.createElement('template')
  tpl.innerHTML = html
  tpl.content.querySelectorAll(`span[data-unfold="${anchor}"]`).forEach((span) => {
    span.replaceWith(...Array.from(span.childNodes))
  })
  return tpl.innerHTML
}

/** Nothing typed yet — no title and only empty paragraphs. */
export function isUntouched(w: { title: string; blocks: Block[] }): boolean {
  return w.title.trim() === '' && w.blocks.every((b) => b.type === 'text' && isBlockEmpty(b))
}

/** A thought is "link only" when it is basically just a URL. */
function isLinkOnly(t: Thought): boolean {
  if (!t.source_url) return false
  const body = t.body.trim()
  return body === '' || body === t.source_url
}

/**
 * Snapshot a saved thought as a block. Photos need a readable URL, resolved by
 * the caller (the backend copies private thought images somewhere public).
 */
export function thoughtToBlock(t: Thought, imageSrc?: string | null): ThoughtBlock | ImageBlock | LinkCardBlock {
  const meta = { thoughtId: t.id, capturedAt: t.created_at, kind: thoughtKind(t) }
  if (t.image_path && imageSrc) {
    return { id: newBlockId(), type: 'image', src: imageSrc, caption: t.body || t.title || '', ...meta }
  }
  if (isLinkOnly(t)) {
    return {
      id: newBlockId(),
      type: 'linkcard',
      url: t.source_url!,
      title: t.title || t.source_title || hostOf(t.source_url) || t.source_url!,
    }
  }
  const hasSource = !!(t.source_url || t.source_title)
  return {
    id: newBlockId(),
    type: 'thought',
    text: t.body || t.title || '',
    ...(hasSource ? { source: { title: t.source_title ?? t.title ?? undefined, url: t.source_url ?? undefined } } : {}),
    ...meta,
  }
}

/**
 * Insert blocks after `afterId` (or at the end). An empty paragraph used as the
 * anchor is replaced rather than left behind, and the document always ends in a
 * paragraph so there is somewhere to keep typing.
 */
export function insertAfter(blocks: Block[], afterId: string | null, inserted: Block[]): Block[] {
  let i = afterId ? blocks.findIndex((b) => b.id === afterId) : -1
  if (i < 0) {
    i = blocks.length - 1
    // Appending: land before a trailing empty paragraph, not after it.
    while (i >= 0 && blocks[i].type === 'text' && isBlockEmpty(blocks[i])) i--
  }
  const out = blocks.slice()
  const anchor = out[i]
  if (anchor && anchor.type === 'text' && isBlockEmpty(anchor) && afterId === anchor.id) {
    out.splice(i, 1, ...inserted)
    i = i + inserted.length - 1
  } else {
    out.splice(i + 1, 0, ...inserted)
    i = i + inserted.length
  }
  const next = out[i + 1]
  if (!next || next.type !== 'text') out.splice(i + 1, 0, emptyText())
  return out
}

export function ensureTrailingText(blocks: Block[]): Block[] {
  const last = blocks[blocks.length - 1]
  return last && last.type === 'text' ? blocks : [...blocks, emptyText()]
}

export function usedThoughtIds(blocks: Block[]): Set<string> {
  const ids = new Set<string>()
  for (const b of blocks) if ((isThoughtLike(b) || b.type === 'image') && b.thoughtId) ids.add(b.thoughtId)
  return ids
}

/** Where a thought-like block came from, as its source line reads ("Burrow · A thinking space" for the author's own). */
export function thoughtSourceLabel(b: ThoughtLikeBlock): string {
  if (b.source?.title) return b.source.title
  if (b.source?.url) return hostOf(b.source.url) || b.source.url
  return 'Burrow · A thinking space'
}

/** A thought snapshot as the text of a thought-like block (margin note, pin, unfold). */
export function thoughtSnapshot(t: Thought): Pick<ThoughtBlock, 'text' | 'source' | 'thoughtId' | 'capturedAt' | 'kind'> {
  const hasSource = !!(t.source_url || t.source_title)
  return {
    text: t.body || t.title || t.source_title || t.source_url || '',
    ...(hasSource ? { source: { title: t.source_title ?? t.title ?? undefined, url: t.source_url ?? undefined } } : {}),
    thoughtId: t.id,
    capturedAt: t.created_at,
    kind: thoughtKind(t),
  }
}

export interface SourceEntry {
  title: string
  url?: string
  site: string
}

/** Numbered "Sources in this writing": thought sources then link cards, de-duplicated. */
export function collectSources(blocks: Block[]): SourceEntry[] {
  const seen = new Set<string>()
  const out: SourceEntry[] = []
  const add = (title: string | undefined, url: string | undefined) => {
    const key = (url || title || '').toLowerCase()
    if (!key || seen.has(key)) return
    seen.add(key)
    out.push({ title: title || hostOf(url) || url || '', url, site: hostOf(url) })
  }
  for (const b of blocks) {
    if (isThoughtLike(b) && b.source) add(b.source.title, b.source.url)
    if (b.type === 'linkcard') add(b.title, b.url)
  }
  return out
}

/** Embedded thoughts readers would see with no idea where they came from. */
export function unsourcedThoughts(blocks: Block[]): ThoughtLikeBlock[] {
  return blocks.filter(
    (b): b is ThoughtLikeBlock => isThoughtLike(b) && !b.own && !(b.source?.title || b.source?.url),
  )
}

/** Convert a thought snapshot into the paragraph or pull quote the author wants instead. */
export function thoughtAsHtml(b: ThoughtBlock): string {
  return escapeHtml(b.text)
}

/**
 * Today's-reflection body: an opening paragraph, then each of the day's
 * thoughts in the order they were saved with room to write between them.
 */
export function reflectionBlocks(thoughts: Array<{ thought: Thought; imageSrc?: string | null }>): Block[] {
  const ordered = [...thoughts].sort((a, b) => a.thought.created_at.localeCompare(b.thought.created_at))
  const blocks: Block[] = [emptyText()]
  for (const { thought, imageSrc } of ordered) blocks.push(thoughtToBlock(thought, imageSrc), emptyText())
  return blocks
}
